import React, { useMemo, useState, useEffect, useRef } from "react";
import AudioTrimmer from "./AudioTrimmer";

const GOOGLE_FONT_FAMILIES = [
  "Montserrat:wght@400;600;700;800",
  "Bebas+Neue",
  "Oswald:wght@400;600;700",
  "Walter+Turncoat:wght@400;600;700;800",
  "Lato:wght@400;700;900",
  "Poppins:wght@400;600;700;800",
  "Just+Another+Hand"
];

const GOOGLE_FONTS_IMPORT_URL = `https://fonts.googleapis.com/css2?family=${GOOGLE_FONT_FAMILIES.join("&family=")}&display=swap`;

const FONT_OPTIONS = [
  "Montserrat",
  "Bebas Neue",
  "Oswald",
  "Walter Turncoat",
  "Lato",
  "Poppins",
  "Futura",
  "Helvetica",
  "Just Another Hand"
];

// Preset templates for quick start
const PRESETS = [
  {
    id: "classic",
    name: "Classic Vinyl",
    emoji: "🎵",
    description: "Clean white text, classic look",
    settings: {
      textColor: "#ffffff",
      textFont: "Montserrat",
      textSize: 48,
      rotationSpeed: 1.0
    },
    images: {
      centrino: "/presets/centrino/classic.png",
      background: "/presets/backgrounds/classic.jpg"
    }
  },
  {
    id: "retro",
    name: "Retro Vibes",
    emoji: "✨",
    description: "Bold retro style with yellow glow",
    settings: {
      textColor: "#ffd700",
      textFont: "Bebas Neue",
      textSize: 56,
      rotationSpeed: 0.8
    },
    images: {
      centrino: "/presets/centrino/retro.png",
      background: "/presets/backgrounds/retro.jpg"
    }
  },
  {
    id: "neon",
    name: "Neon Nights",
    emoji: "💜",
    description: "Purple neon aesthetic",
    settings: {
      textColor: "#a78bfa",
      textFont: "Oswald",
      textSize: 52,
      rotationSpeed: 1.5
    },
    images: {
      centrino: "/presets/centrino/neon.png",
      background: "/presets/backgrounds/neon.jpg"
    }
  },
  {
    id: "minimal",
    name: "Minimal",
    emoji: "⚪",
    description: "Simple and elegant",
    settings: {
      textColor: "#f0f0f0",
      textFont: "Lato",
      textSize: 44,
      rotationSpeed: 0.7
    },
    images: {
      centrino: "/presets/centrino/minimal.png",
      background: "/presets/backgrounds/minimal.jpg"
    }
  },
  {
    id: "energetic",
    name: "High Energy",
    emoji: "⚡",
    description: "Fast spin, cyan color",
    settings: {
      textColor: "#26d6d9",
      textFont: "Poppins",
      textSize: 50,
      rotationSpeed: 2.5
    },
    images: {
      centrino: "/presets/centrino/energetic.png",
      background: "/presets/backgrounds/energetic.jpg"
    }
  },
  {
    id: "handwritten",
    name: "Handwritten",
    emoji: "✍️",
    description: "Casual handwritten feel",
    settings: {
      textColor: "#ffc107",
      textFont: "Walter Turncoat",
      textSize: 54,
      rotationSpeed: 1.2
    },
    images: {
      centrino: "/presets/centrino/handwritten.png",
      background: "/presets/backgrounds/handwritten.jpg"
    }
  }
];

// Icon components
const UploadIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
    <polyline points="17 8 12 3 7 8"/>
    <line x1="12" y1="3" x2="12" y2="15"/>
  </svg>
);

const ImageIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" viewBox="0 0 16 16">
    <path d="M6.002 5.5a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0z"/>
    <path d="M2.002 1a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V3a2 2 0 0 0-2-2h-12zm12 1a1 1 0 0 1 1 1v6.5l-3.777-1.947a.5.5 0 0 0-.577.093l-3.71 3.71-2.66-1.772a.5.5 0 0 0-.63.062L1.002 12V3a1 1 0 0 1 1-1h12z"/>
  </svg>
);

const MusicIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" viewBox="0 0 16 16">
    <path d="M6 13c0 1.105-1.12 2-2.5 2S1 14.105 1 13c0-1.104 1.12-2 2.5-2s2.5.896 2.5 2zm9-2c0 1.105-1.12 2-2.5 2s-2.5-.895-2.5-2 1.12-2 2.5-2 2.5.895 2.5 2z"/>
    <path fillRule="evenodd" d="M14 11V2h1v9h-1zM6 3v10H5V3h1z"/>
    <path d="M5 2.905a1 1 0 0 1 .9-.995l8-.8a1 1 0 0 1 1.1.995V3L5 4V2.905z"/>
  </svg>
);

const CheckIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
    <path d="M13.854 3.646a.5.5 0 0 1 0 .708l-7 7a.5.5 0 0 1-.708 0l-3.5-3.5a.5.5 0 1 1 .708-.708L6.5 10.293l6.646-6.647a.5.5 0 0 1 .708 0z"/>
  </svg>
);

const PlayIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" viewBox="0 0 16 16">
    <path d="m11.596 8.697-6.363 3.692c-.54.313-1.233-.066-1.233-.697V4.308c0-.63.692-1.01 1.233-.696l6.363 3.692a.802.802 0 0 1 0 1.393z"/>
  </svg>
);

export default function VideoForm({ apiUrl, showBaseUrl, authenticityToken, isFreeTier = false, freeTierMaxDuration = 10 }) {
  const [title, setTitle] = useState("");
  const [artist, setArtist] = useState("");
  const [showTitle, setShowTitle] = useState(true);
  const [showArtist, setShowArtist] = useState(true);
  const [textColor, setTextColor] = useState("#ffffff");
  const [textFont, setTextFont] = useState(FONT_OPTIONS[0]);
  const [textSize, setTextSize] = useState(48);
  const [duration, setDuration] = useState(isFreeTier ? freeTierMaxDuration : 30);
  const [rotationSpeed, setRotationSpeed] = useState(1.0);

  const maxDuration = isFreeTier ? freeTierMaxDuration : 300;
  const [centrino, setCentrino] = useState(null);
  const [audio, setAudio] = useState(null);
  const [background, setBackground] = useState(null);
  const [audioPreview, setAudioPreview] = useState(null);
  const [centrinoPreview, setCentrinoPreview] = useState(null);
  const [backgroundPreview, setBackgroundPreview] = useState(null);
  const [centrinoIsVideo, setCentrinoIsVideo] = useState(false);
  const [audioLength, setAudioLength] = useState(null);
  const [audioStart, setAudioStart] = useState(0);
  const [audioClipDuration, setAudioClipDuration] = useState(30);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  const centrinoInputRef = useRef(null);
  const backgroundInputRef = useRef(null);
  const audioInputRef = useRef(null);

  const displayTitle = (title || "").trim() || "Title";
  const displayArtist = (artist || "").trim() || "Artist";

  useEffect(() => () => {
    if (centrinoPreview) URL.revokeObjectURL(centrinoPreview);
    if (backgroundPreview) URL.revokeObjectURL(backgroundPreview);
    if (audioPreview) URL.revokeObjectURL(audioPreview);
  }, [centrinoPreview, backgroundPreview, audioPreview]);

  useEffect(() => {
    const effectiveMax = Math.min(duration, maxDuration);
    setAudioClipDuration((prev) => {
      const fallback = effectiveMax;
      const base = Number.isFinite(prev) && prev > 0 ? prev : fallback;
      if (audioLength) {
        return Math.min(base, audioLength, fallback);
      }
      return Math.min(base, fallback);
    });
    setAudioStart((prev) => {
      if (audioLength && prev > audioLength) return Math.max(audioLength - 1, 0);
      return prev;
    });
  }, [duration, audioLength, maxDuration]);

  const rotationDuration = useMemo(() => {
    const speed = parseFloat(rotationSpeed || 1);
    return speed > 0 ? Math.max(0.5, 10 / speed) : 10;
  }, [rotationSpeed]);

  const handleFileChange = (setter, previewSetter) => (event) => {
    const file = event.target.files?.[0];
    setter(file || null);
    if (previewSetter) {
      if (file) {
        const url = URL.createObjectURL(file);
        previewSetter(url);
      } else {
        previewSetter(null);
      }
    }
  };

  const handleCentrinoChange = (event) => {
    const file = event.target.files?.[0];
    setCentrino(file || null);
    if (centrinoPreview) URL.revokeObjectURL(centrinoPreview);
    if (file) {
      const url = URL.createObjectURL(file);
      setCentrinoPreview(url);
      // Detect if it's a video file
      const isVideo = file.type.startsWith('video/');
      setCentrinoIsVideo(isVideo);
    } else {
      setCentrinoPreview(null);
      setCentrinoIsVideo(false);
    }
  };

  const handleAudioChange = (event) => {
    const file = event.target.files?.[0];
    setAudio(file || null);
    if (audioPreview) URL.revokeObjectURL(audioPreview);
    if (file) {
      const url = URL.createObjectURL(file);
      setAudioPreview(url);
    } else {
      setAudioPreview(null);
      setAudioLength(null);
      setAudioStart(0);
      setAudioClipDuration(duration);
    }
  };

  // Helper function to convert URL to File object
  const urlToFile = async (url, filename, mimeType) => {
    try {
      const response = await fetch(url);
      if (!response.ok) return null;
      const blob = await response.blob();
      return new File([blob], filename, { type: mimeType });
    } catch (error) {
      console.error(`Error loading preset image: ${url}`, error);
      return null;
    }
  };

  const applyPreset = async (preset) => {
    // Apply text settings immediately
    setTextColor(preset.settings.textColor);
    setTextFont(preset.settings.textFont);
    setTextSize(preset.settings.textSize);
    setRotationSpeed(preset.settings.rotationSpeed);

    // Load preset images if available
    if (preset.images) {
      // Clear previews first to force re-render
      if (centrinoPreview) {
        URL.revokeObjectURL(centrinoPreview);
        setCentrinoPreview(null);
      }
      if (backgroundPreview) {
        URL.revokeObjectURL(backgroundPreview);
        setBackgroundPreview(null);
      }

      // Small delay to ensure React processes the null state
      await new Promise(resolve => setTimeout(resolve, 50));

      // Load centrino image
      if (preset.images.centrino) {
        const centrinoFile = await urlToFile(
          preset.images.centrino,
          `${preset.id}-centrino.png`,
          'image/png'
        );
        if (centrinoFile) {
          const newUrl = URL.createObjectURL(centrinoFile);
          setCentrino(centrinoFile);
          setCentrinoIsVideo(false);
          setCentrinoPreview(newUrl);
        }
      }

      // Load background image
      if (preset.images.background) {
        const backgroundFile = await urlToFile(
          preset.images.background,
          `${preset.id}-background.jpg`,
          'image/jpeg'
        );
        if (backgroundFile) {
          const newUrl = URL.createObjectURL(backgroundFile);
          setBackground(backgroundFile);
          setBackgroundPreview(newUrl);
        }
      }
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError(null);
    setResult(null);

    if (!centrino || !audio) {
      setError("Vinyl label and audio are required");
      return;
    }

    const formData = new FormData();
    formData.append("title", title);
    formData.append("artist", artist);
    formData.append("show_title", showTitle);
    formData.append("show_artist", showArtist);
    formData.append("duration", duration);
    formData.append("rotation_speed", rotationSpeed);
    formData.append("text_color", textColor);
    formData.append("text_font", textFont);
    formData.append("text_size", textSize);
    formData.append("centrino", centrino);
    formData.append("audio", audio);
    formData.append("audio_start", audioStart);
    const safeAudioDuration = audioClipDuration && audioClipDuration > 0 ? audioClipDuration : duration;
    formData.append("audio_duration", safeAudioDuration);
    if (background) formData.append("background", background);

    setLoading(true);
    try {
      const response = await fetch(apiUrl, {
        method: "POST",
        headers: {
          ...(authenticityToken ? { "X-CSRF-Token": authenticityToken } : {}),
        },
        body: formData,
      });

      const data = await response.json();
      if (!response.ok) {
        const message = data?.errors?.join(", ") || "Error creating video";
        throw new Error(message);
      }

      window.location.href = `${showBaseUrl}/${data.id}`;
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  return (
    <form className="vf-form" onSubmit={handleSubmit}>
      <style>{`
        @import url('${GOOGLE_FONTS_IMPORT_URL}');

        .vf-form { display: grid; grid-template-columns: 1fr 1fr; gap: 2rem; align-items: start; }
        @media (max-width: 992px) { .vf-form { grid-template-columns: 1fr; } }

        .vf-section { margin-bottom: 1.5rem; }
        .vf-section-title {
          font-size: 0.7rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.1em;
          color: rgba(255,255,255,0.4);
          margin-bottom: 0.75rem;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }
        .vf-section-title svg { opacity: 0.6; }

        .vf-input-group { display: grid; grid-template-columns: 1fr 1fr; gap: 1rem; margin-bottom: 1rem; }
        .vf-input-group.full { grid-template-columns: 1fr; }
        @media (max-width: 576px) { .vf-input-group { grid-template-columns: 1fr; } }

        .vf-field { display: flex; flex-direction: column; gap: 0.5rem; }
        .vf-label { font-size: 0.85rem; font-weight: 500; color: rgba(255,255,255,0.8); display: flex; align-items: center; gap: 0.5rem; }
        .vf-label .badge { font-size: 0.65rem; padding: 0.2rem 0.5rem; }

        .vf-input {
          background: rgba(15, 20, 45, 0.8);
          border: 1px solid rgba(255,255,255,0.1);
          border-radius: 10px;
          color: #fff;
          padding: 0.75rem 1rem;
          font-size: 0.95rem;
          transition: all 0.2s ease;
          width: 100%;
        }
        .vf-input:focus {
          outline: none;
          border-color: rgba(95, 63, 241, 0.5);
          box-shadow: 0 0 0 3px rgba(95, 63, 241, 0.15);
          background: rgba(15, 20, 45, 0.9);
        }
        .vf-input::placeholder { color: rgba(255,255,255,0.4); }
        .vf-input[type="color"] { padding: 0.25rem; height: 42px; cursor: pointer; }
        .vf-input[type="file"] { display: none; }

        .vf-select {
          background: rgba(15, 20, 45, 0.8);
          border: 1px solid rgba(255,255,255,0.1);
          border-radius: 10px;
          color: #fff;
          padding: 0.75rem 1rem;
          font-size: 0.95rem;
          cursor: pointer;
          transition: all 0.2s ease;
        }
        .vf-select:focus {
          outline: none;
          border-color: rgba(95, 63, 241, 0.5);
          box-shadow: 0 0 0 3px rgba(95, 63, 241, 0.15);
        }
        .vf-select option { background: #0f0f1a; color: #fff; }

        .vf-upload-zone {
          border: 2px dashed rgba(255,255,255,0.15);
          border-radius: 12px;
          padding: 1.5rem;
          text-align: center;
          cursor: pointer;
          transition: all 0.3s ease;
          background: rgba(15, 20, 45, 0.4);
        }
        .vf-upload-zone:hover {
          border-color: rgba(95, 63, 241, 0.5);
          background: rgba(95, 63, 241, 0.05);
        }
        .vf-upload-zone.has-file {
          border-color: rgba(34, 197, 94, 0.5);
          background: rgba(34, 197, 94, 0.05);
        }
        .vf-upload-zone.required {
          border-color: rgba(95, 63, 241, 0.3);
        }
        .vf-upload-icon {
          width: 48px;
          height: 48px;
          margin: 0 auto 0.75rem;
          background: linear-gradient(135deg, rgba(95, 63, 241, 0.15), rgba(38, 214, 217, 0.1));
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #a78bfa;
        }
        .vf-upload-zone.has-file .vf-upload-icon {
          background: rgba(34, 197, 94, 0.15);
          color: #22c55e;
        }
        .vf-upload-title { font-weight: 600; margin-bottom: 0.25rem; font-size: 0.9rem; }
        .vf-upload-subtitle { font-size: 0.8rem; color: rgba(255,255,255,0.5); }
        .vf-upload-filename { font-size: 0.8rem; color: #22c55e; margin-top: 0.5rem; display: flex; align-items: center; justify-content: center; gap: 0.35rem; }

        .vf-audio-section {
          background: rgba(15, 20, 45, 0.5);
          border: 1px solid rgba(255,255,255,0.08);
          border-radius: 12px;
          padding: 1rem;
          margin-top: 0.75rem;
        }

        .vf-color-picker {
          display: flex;
          align-items: center;
          gap: 0.75rem;
        }
        .vf-color-preview {
          width: 42px;
          height: 42px;
          border-radius: 10px;
          border: 2px solid rgba(255,255,255,0.2);
          cursor: pointer;
          transition: all 0.2s ease;
        }
        .vf-color-preview:hover { border-color: rgba(255,255,255,0.4); }
        .vf-color-input { flex: 1; }

        .vf-error {
          background: rgba(239, 68, 68, 0.1);
          border: 1px solid rgba(239, 68, 68, 0.3);
          border-radius: 12px;
          padding: 1rem;
          color: #f87171;
          display: flex;
          align-items: center;
          gap: 0.75rem;
          margin-bottom: 1rem;
        }

        .vf-submit-area {
          display: flex;
          align-items: center;
          gap: 1rem;
          padding-top: 0.5rem;
        }
        .vf-submit-btn {
          background: linear-gradient(135deg, #5f3ff1, #26d6d9);
          border: none;
          color: #fff;
          padding: 0.875rem 2rem;
          border-radius: 12px;
          font-weight: 600;
          font-size: 1rem;
          cursor: pointer;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          box-shadow: 0 4px 20px rgba(95, 63, 241, 0.3);
        }
        .vf-submit-btn:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 6px 25px rgba(95, 63, 241, 0.4);
        }
        .vf-submit-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
        .vf-submit-hint { font-size: 0.85rem; color: rgba(255,255,255,0.5); }

        .vf-checkbox {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          cursor: pointer;
          font-size: 0.9rem;
          color: rgba(255,255,255,0.8);
        }
        .vf-checkbox input[type="checkbox"] {
          width: 18px;
          height: 18px;
          accent-color: #5f3ff1;
          cursor: pointer;
        }

        /* Preview */
        .vf-preview-container { position: sticky; top: 1rem; }
        .vinyl-preview { position: relative; width: 100%; padding-top: 100%; border-radius: 20px; overflow: hidden; background: #0c0c10; border: 1px solid rgba(255,255,255,0.08); }
        .vinyl-preview__bg { position: absolute; inset: 0; background-size: cover; background-position: center; filter: blur(4px) brightness(0.6); }
        .vinyl-preview__disc { position: absolute; top: 50%; left: 50%; width: 70%; height: 70%; border-radius: 50%; transform: translate(-50%, -50%); box-shadow: 0 10px 25px rgba(0,0,0,0.45); animation: vinyl-spin linear infinite; background: linear-gradient(135deg, rgba(95, 63, 241, 0.4), rgba(38, 214, 217, 0.45), rgba(255, 107, 206, 0.5)); overflow: hidden; }
        .vinyl-preview__disc img,
        .vinyl-preview__disc video { position: absolute; top: 0; left: 0; width: 100%; height: 100%; object-fit: cover; border-radius: 50%; z-index: 1; }
        .vinyl-preview__title { position: absolute; top: 15%; left: 50%; transform: translateX(-50%); color: var(--text-color, #fff); text-align: center; padding: 0 8px; font-weight: 800; letter-spacing: 0.04em; text-transform: none; z-index: 3; line-height: 1.1; text-shadow: 0 2px 6px rgba(0,0,0,0.5); }
        .vinyl-preview__artist { position: absolute; top: 28%; left: 50%; transform: translateX(-50%); color: var(--text-color, #fff); text-align: center; padding: 0 8px; font-weight: 600; letter-spacing: 0.04em; text-transform: none; z-index: 3; line-height: 1.1; text-shadow: 0 2px 6px rgba(0,0,0,0.5); opacity: 0.95; }
        .vinyl-preview__placeholder { position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); color: rgba(255,255,255,0.85); font-size: 0.9rem; text-align: center; padding: 0 12px; z-index: 2; width: 80%; }
        @keyframes vinyl-spin { from { transform: translate(-50%, -50%) rotate(0deg); } to { transform: translate(-50%, -50%) rotate(360deg); } }

        /* Preset styles */
        .vf-presets-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(140px, 1fr)); gap: 0.75rem; }
        .vf-preset-card {
          background: rgba(15, 20, 45, 0.5);
          border: 2px solid rgba(255,255,255,0.1);
          border-radius: 12px;
          padding: 0.875rem;
          cursor: pointer;
          transition: all 0.25s ease;
          text-align: center;
        }
        .vf-preset-card:hover {
          border-color: rgba(95, 63, 241, 0.5);
          background: rgba(95, 63, 241, 0.1);
          transform: translateY(-2px);
        }
        .vf-preset-emoji { font-size: 1.5rem; margin-bottom: 0.5rem; }
        .vf-preset-name { font-size: 0.85rem; font-weight: 600; margin-bottom: 0.25rem; }
        .vf-preset-desc { font-size: 0.7rem; color: rgba(255,255,255,0.5); line-height: 1.3; }
      `}</style>

      {/* LEFT COLUMN - Form */}
      <div className="vf-form-column">
        {/* Presets Section */}
        <div className="vf-section">
          <div className="vf-section-title">
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" viewBox="0 0 16 16">
              <path d="M8 4.754a3.246 3.246 0 1 0 0 6.492 3.246 3.246 0 0 0 0-6.492zM5.754 8a2.246 2.246 0 1 1 4.492 0 2.246 2.246 0 0 1-4.492 0z"/>
              <path d="M9.796 1.343c-.527-1.79-3.065-1.79-3.592 0l-.094.319a.873.873 0 0 1-1.255.52l-.292-.16c-1.64-.892-3.433.902-2.54 2.541l.159.292a.873.873 0 0 1-.52 1.255l-.319.094c-1.79.527-1.79 3.065 0 3.592l.319.094a.873.873 0 0 1 .52 1.255l-.16.292c-.892 1.64.901 3.434 2.541 2.54l.292-.159a.873.873 0 0 1 1.255.52l.094.319c.527 1.79 3.065 1.79 3.592 0l.094-.319a.873.873 0 0 1 1.255-.52l.292.16c1.64.893 3.434-.902 2.54-2.541l-.159-.292a.873.873 0 0 1 .52-1.255l.319-.094c1.79-.527 1.79-3.065 0-3.592l-.319-.094a.873.873 0 0 1-.52-1.255l.16-.292c.893-1.64-.902-3.433-2.541-2.54l-.292.159a.873.873 0 0 1-1.255-.52l-.094-.319z"/>
            </svg>
            Quick Start Templates
          </div>
          <div className="vf-presets-grid">
            {PRESETS.map(preset => (
              <div
                key={preset.id}
                className="vf-preset-card"
                onClick={() => applyPreset(preset)}
              >
                <div className="vf-preset-emoji">{preset.emoji}</div>
                <div className="vf-preset-name">{preset.name}</div>
                <div className="vf-preset-desc">{preset.description}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Track Info Section */}
        <div className="vf-section">
          <div className="vf-section-title">
            <MusicIcon /> Track Information
          </div>
          <div className="vf-input-group">
            <div className="vf-field">
              <label className="vf-label">Title</label>
              <input
                className="vf-input"
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g. Summer Vibes"
                disabled={!showTitle}
              />
            </div>
            <div className="vf-field">
              <label className="vf-label">Artist</label>
              <input
                className="vf-input"
                type="text"
                value={artist}
                onChange={(e) => setArtist(e.target.value)}
                placeholder="e.g. DJ Looper"
                disabled={!showArtist}
              />
            </div>
          </div>
          <div className="vf-input-group" style={{ marginTop: '0.5rem' }}>
            <label className="vf-checkbox">
              <input
                type="checkbox"
                checked={showTitle}
                onChange={(e) => setShowTitle(e.target.checked)}
              />
              <span>Show title</span>
            </label>
            <label className="vf-checkbox">
              <input
                type="checkbox"
                checked={showArtist}
                onChange={(e) => setShowArtist(e.target.checked)}
              />
              <span>Show artist</span>
            </label>
          </div>
        </div>

        {/* Text Styling Section */}
        <div className="vf-section">
          <div className="vf-section-title">
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" viewBox="0 0 16 16">
              <path d="M12.258 3h-8.51l-.083 2.46h.479c.26-1.544.758-1.783 2.693-1.845l.424-.013v7.827c0 .663-.144.82-1.3.923v.52h4.082v-.52c-1.162-.103-1.306-.26-1.306-.923V3.602l.431.013c1.934.062 2.434.301 2.693 1.846h.479L12.258 3z"/>
            </svg>
            Text Styling
          </div>
          <div className="vf-input-group">
            <div className="vf-field">
              <label className="vf-label">Text Color</label>
              <div className="vf-color-picker">
                <input
                  type="color"
                  className="vf-color-preview"
                  value={textColor}
                  onChange={(e) => setTextColor(e.target.value)}
                />
                <input
                  className="vf-input vf-color-input"
                  type="text"
                  value={textColor}
                  onChange={(e) => setTextColor(e.target.value)}
                  placeholder="#ffffff"
                />
              </div>
            </div>
            <div className="vf-field">
              <label className="vf-label">Font</label>
              <select className="vf-select" value={textFont} onChange={(e) => setTextFont(e.target.value)}>
                {FONT_OPTIONS.map((font) => (
                  <option key={font} value={font} style={{ fontFamily: font }}>{font}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="vf-input-group">
            <div className="vf-field">
              <label className="vf-label">Font Size</label>
              <input
                className="vf-input"
                type="number"
                min="24"
                max="96"
                step="2"
                value={textSize}
                onChange={(e) => setTextSize(Number(e.target.value))}
              />
            </div>
            <div className="vf-field">
              <label className="vf-label">Rotation Speed</label>
              <input
                className="vf-input"
                type="number"
                min="0.1"
                max="5"
                step="0.1"
                value={rotationSpeed}
                onChange={(e) => setRotationSpeed(e.target.value)}
              />
            </div>
          </div>
        </div>

        {/* Video Settings Section */}
        <div className="vf-section">
          <div className="vf-section-title">
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" viewBox="0 0 16 16">
              <path d="M0 5a2 2 0 0 1 2-2h7.5a2 2 0 0 1 1.983 1.738l3.11-1.382A1 1 0 0 1 16 4.269v7.462a1 1 0 0 1-1.406.913l-3.111-1.382A2 2 0 0 1 9.5 13H2a2 2 0 0 1-2-2V5z"/>
            </svg>
            Video Settings
          </div>
          <div className="vf-input-group full">
            <div className="vf-field">
              <label className="vf-label">
                Duration (seconds)
                {isFreeTier && (
                  <span className="badge" style={{ background: 'rgba(234, 179, 8, 0.2)', color: '#eab308', border: '1px solid rgba(234, 179, 8, 0.3)' }}>
                    Max {freeTierMaxDuration}s
                  </span>
                )}
              </label>
              <input
                className="vf-input"
                type="number"
                min="5"
                max={maxDuration}
                step="1"
                value={duration}
                onChange={(e) => setDuration(Math.min(Number(e.target.value), maxDuration))}
              />
            </div>
          </div>
        </div>

        {/* Media Upload Section */}
        <div className="vf-section">
          <div className="vf-section-title">
            <UploadIcon /> Media Files
          </div>

          <div className="vf-input-group">
            {/* Vinyl Label Upload */}
            <div
              className={`vf-upload-zone required ${centrino ? 'has-file' : ''}`}
              onClick={() => centrinoInputRef.current?.click()}
            >
              <input
                ref={centrinoInputRef}
                type="file"
                accept="image/*,video/*"
                onChange={handleCentrinoChange}
                className="vf-input"
              />
              <div className="vf-upload-icon">
                {centrino ? <CheckIcon /> : <ImageIcon />}
              </div>
              <div className="vf-upload-title">Vinyl Label *</div>
              <div className="vf-upload-subtitle">Image or Video</div>
              {centrino && (
                <div className="vf-upload-filename">
                  <CheckIcon /> {centrino.name}
                </div>
              )}
            </div>

            {/* Background Upload */}
            <div
              className={`vf-upload-zone ${background ? 'has-file' : ''}`}
              onClick={() => backgroundInputRef.current?.click()}
            >
              <input
                ref={backgroundInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileChange(setBackground, setBackgroundPreview)}
                className="vf-input"
              />
              <div className="vf-upload-icon">
                {background ? <CheckIcon /> : <ImageIcon />}
              </div>
              <div className="vf-upload-title">Background</div>
              <div className="vf-upload-subtitle">Optional</div>
              {background && (
                <div className="vf-upload-filename">
                  <CheckIcon /> {background.name}
                </div>
              )}
            </div>
          </div>

          {/* Audio Upload */}
          <div
            className={`vf-upload-zone required ${audio ? 'has-file' : ''}`}
            onClick={() => audioInputRef.current?.click()}
            style={{ marginTop: '1rem' }}
          >
            <input
              ref={audioInputRef}
              type="file"
              accept="audio/*"
              onChange={handleAudioChange}
              className="vf-input"
            />
            <div className="vf-upload-icon">
              {audio ? <CheckIcon /> : <MusicIcon />}
            </div>
            <div className="vf-upload-title">Audio Track *</div>
            <div className="vf-upload-subtitle">MP3, WAV, AAC or other formats</div>
            {audio && (
              <div className="vf-upload-filename">
                <CheckIcon /> {audio.name}
              </div>
            )}
          </div>

          {/* Audio Trimmer */}
          {audioPreview && audioLength && (
            <div className="vf-audio-section">
              <AudioTrimmer
                audioUrl={audioPreview}
                audioDuration={audioLength}
                maxDuration={Math.min(duration, maxDuration)}
                audioStart={audioStart}
                clipDuration={audioClipDuration}
                onStartChange={setAudioStart}
                onDurationChange={setAudioClipDuration}
              />
            </div>
          )}
          {audioPreview && !audioLength && (
            <div className="vf-audio-section">
              <audio
                src={audioPreview}
                style={{ display: "none" }}
                onLoadedMetadata={(e) => {
                  const len = e.target.duration || 0;
                  const effectiveMax = Math.min(duration, maxDuration);
                  setAudioLength(len);
                  setAudioClipDuration(Math.min(len || effectiveMax, effectiveMax));
                }}
              />
              <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.85rem' }}>Loading audio...</div>
            </div>
          )}
        </div>

        {/* Error Message */}
        {error && (
          <div className="vf-error">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" viewBox="0 0 16 16">
              <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z"/>
              <path d="M7.002 11a1 1 0 1 1 2 0 1 1 0 0 1-2 0zM7.1 4.995a.905.905 0 1 1 1.8 0l-.35 3.507a.552.552 0 0 1-1.1 0L7.1 4.995z"/>
            </svg>
            <div>{error}</div>
          </div>
        )}

        {/* Submit Button */}
        <div className="vf-submit-area">
          <button className="vf-submit-btn" type="submit" disabled={loading}>
            {loading ? (
              <>
                <span className="spinner-border spinner-border-sm" role="status" />
                Processing...
              </>
            ) : (
              <>
                <PlayIcon />
                Generate Video
              </>
            )}
          </button>
          <span className="vf-submit-hint">Video renders in background</span>
        </div>
      </div>

      {/* RIGHT COLUMN - Preview */}
      <div className="vf-preview-container">
        <div className="vinyl-preview">
          {backgroundPreview && (
            <div
              className="vinyl-preview__bg"
              style={{ backgroundImage: `url(${backgroundPreview})` }}
            />
          )}
          <div
            className="vinyl-preview__disc"
            style={{
              animationDuration: `${rotationDuration}s`,
              "--text-color": textColor,
              fontFamily: `${textFont}, "Helvetica Neue", Arial, sans-serif`,
            }}
          >
            {centrinoPreview ? (
              centrinoIsVideo ? (
                <video
                  src={centrinoPreview}
                  autoPlay
                  loop
                  muted
                  playsInline
                />
              ) : (
                <img src={centrinoPreview} alt="Vinyl label preview" />
              )
            ) : (
              <span className="vinyl-preview__placeholder">Upload a vinyl label to see the preview</span>
            )}
            {showTitle && (
              <div className="vinyl-preview__title" style={{ fontSize: `${textSize * 0.6}px` }}>
                {displayTitle}
              </div>
            )}
            {showArtist && (
              <div className="vinyl-preview__artist" style={{ fontSize: `${textSize * 0.6 * 0.75}px` }}>
                {displayArtist}
              </div>
            )}
          </div>
        </div>
        <p className="text-secondary small mt-2 mb-0">
          Simulated preview (rotation {rotationSpeed}x). Audio is not played in preview.
        </p>
      </div>
    </form>
  );
}
